// Helper function to calculate median
export const median = (arr) => {
    if (arr.length === 0) return 0;
    const sorted = [...arr].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

// Enhanced linear regression with better numerical stability
export const linearRegression = (data) => {
    const n = data.length;
    if (n < 2) return null;
    
    try {
        let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0;
        
        for (let i = 0; i < n; i++) {
            const x = data[i].x;
            const y = data[i].y;
            
            if (!isFinite(x) || !isFinite(y)) continue;
            
            sumX += x;
            sumY += y;
            sumXY += x * y;
            sumX2 += x * x;
        }
        
        const denominator = n * sumX2 - sumX * sumX;
        
        // Handle near-zero denominator more gracefully
        if (Math.abs(denominator) < 1e-10) {
            // Return horizontal line through mean
            return { slope: 0, intercept: sumY / n };
        }
        
        const slope = (n * sumXY - sumX * sumY) / denominator;
        const intercept = (sumY - slope * sumX) / n;
        
        // Check for valid coefficients
        if (!isFinite(slope) || !isFinite(intercept)) {
            // Fallback to mean
            return { slope: 0, intercept: sumY / n };
        }
        
        return { slope, intercept };
    } catch (error) {
        console.error('Linear regression error:', error);
        return null;
    }
};

// Weighted linear regression for LOESS
export const weightedLinearRegression = (weightedData) => {
    let sumX = 0, sumY = 0, sumXY = 0, sumX2 = 0, sumW = 0;
    
    for (let i = 0; i < weightedData.length; i++) {
        const { x, y, weight } = weightedData[i];
        if (!isFinite(x) || !isFinite(y) || !isFinite(weight)) continue;
        
        const w = weight;
        sumX += w * x;
        sumY += w * y;
        sumXY += w * x * y;
        sumX2 += w * x * x;
        sumW += w;
    }
    
    if (sumW === 0) return null;
    
    const denominator = sumW * sumX2 - sumX * sumX;
    if (Math.abs(denominator) < 1e-10) return null;
    
    const slope = (sumW * sumXY - sumX * sumY) / denominator;
    const intercept = (sumY - slope * sumX) / sumW;
    
    if (!isFinite(slope) || !isFinite(intercept)) return null;
    
    return { slope, intercept };
};

// Trend Algorithm 1: Global Linear Trend (Optimized for 500+ points)
export const calculateGlobalLinearTrend = (data, metric, helpers) => {
    if (data.length < 2) {
        return data.map(point => point[metric]);
    }

    // Normalize timestamps to prevent numerical issues with large numbers
    const startTime = data[0].timestamp;
    const totalRange = data[data.length - 1].timestamp - startTime;
    
    // Scale to 0-1 range for better numerical stability
    const dataWithNormalizedTime = data.map(point => ({
        ...point,
        normalizedTime: totalRange > 0 ? (point.timestamp - startTime) / totalRange : 0
    }));

    const validData = dataWithNormalizedTime
        .map(p => ({ x: p.normalizedTime, y: p[metric] }))
        .filter(d => isFinite(d.x) && isFinite(d.y));

    if (validData.length < 2) {
        return data.map(point => point[metric]);
    }

    const coefficients = helpers.linearRegression(validData);
    if (!coefficients) {
        // Fallback to EMA for large datasets
        return calculateExponentialMovingAverage(data, metric, 0.1);
    }

    return dataWithNormalizedTime.map(point => 
        coefficients.slope * point.normalizedTime + coefficients.intercept
    );
};

// Trend Algorithm 2: Exponential Moving Average (Smoothing)
export const calculateExponentialMovingAverage = (data, metric, alpha = 0.3) => {
    if (data.length === 0) return [];
    
    const ema = [];
    ema[0] = data[0][metric]; // First value is same as data
    
    for (let i = 1; i < data.length; i++) {
        const currentValue = data[i][metric];
        if (!isFinite(currentValue)) {
            ema[i] = ema[i - 1]; // Carry forward if invalid
        } else {
            ema[i] = alpha * currentValue + (1 - alpha) * ema[i - 1];
        }
    }
    return ema;
};

// Trend Algorithm 3: LOESS (Local Regression - Flexible)
export const calculateLoessTrend = (data, metric, bandwidth = 0.5, helpers) => {
    if (data.length < 3) {
        return data.map(point => point[metric]);
    }

    const startTime = data[0].timestamp;
    const dataWithDays = data.map(point => ({
        ...point,
        daysFromStart: (point.timestamp - startTime) / (1000 * 60 * 60 * 24)
    }));

    return dataWithDays.map((targetPoint, targetIndex) => {
        // Calculate weights for all points based on temporal distance
        const weights = dataWithDays.map((point, index) => {
            const distance = Math.abs(point.daysFromStart - targetPoint.daysFromStart);
            const maxDistance = bandwidth * (data.length / 2);
            const weight = Math.max(0, 1 - Math.pow(distance / maxDistance, 3));
            return Math.pow(weight, 3); // Tricube weight function
        });

        // Perform weighted linear regression for this local point
        const weightedData = dataWithDays
            .map((point, index) => ({
                x: point.daysFromStart,
                y: point[metric],
                weight: weights[index]
            }))
            .filter(d => isFinite(d.x) && isFinite(d.y) && d.weight > 0.001);

        if (weightedData.length < 2) {
            return targetPoint[metric];
        }

        const coefficients = helpers.weightedLinearRegression(weightedData);
        if (!coefficients) {
            return targetPoint[metric];
        }

        return coefficients.slope * targetPoint.daysFromStart + coefficients.intercept;
    });
};

// Trend Algorithm 4: Segmented Linear Regression (Better for 500 points)
export const calculateSegmentedLinearTrend = (data, metric, segments = 5, helpers) => {
    if (data.length < segments * 2) {
        return calculateGlobalLinearTrend(data, metric, helpers);
    }

    const pointsPerSegment = Math.floor(data.length / segments);
    const trendValues = [];
    
    // Process each segment
    for (let seg = 0; seg < segments; seg++) {
        const startIdx = seg * pointsPerSegment;
        const endIdx = (seg === segments - 1) ? data.length : (seg + 1) * pointsPerSegment;
        const segmentData = data.slice(startIdx, endIdx);
        
        const startTime = segmentData[0].timestamp;
        const segmentWithTime = segmentData.map(point => ({
            ...point,
            hoursFromStart: (point.timestamp - startTime) / (1000 * 60 * 60)
        }));

        const validData = segmentWithTime
            .map(p => ({ x: p.hoursFromStart, y: p[metric] }))
            .filter(d => isFinite(d.x) && isFinite(d.y));

        if (validData.length < 2) {
            // Use values as-is if regression not possible
            trendValues.push(...segmentData.map(p => p[metric]));
            continue;
        }

        const coefficients = helpers.linearRegression(validData);
        if (!coefficients) {
            trendValues.push(...segmentData.map(p => p[metric]));
            continue;
        }

        const segmentTrend = segmentWithTime.map(point => 
            coefficients.slope * point.hoursFromStart + coefficients.intercept
        );
        trendValues.push(...segmentTrend);
    }

    return trendValues;
};

// Trend Algorithm 5: Adaptive Trend Detection
export const calculateAdaptiveTrend = (data, metric, helpers) => {
    if (data.length < 10) {
        return calculateGlobalLinearTrend(data, metric, helpers);
    }

    // First, calculate global trend
    const globalTrend = calculateGlobalLinearTrend(data, metric, helpers);
    
    // Calculate R-squared to see if trend is meaningful
    const startTime = data[0].timestamp;
    const totalRange = data[data.length - 1].timestamp - startTime;
    
    const dataWithNormalizedTime = data.map(point => ({
        ...point,
        normalizedTime: totalRange > 0 ? (point.timestamp - startTime) / totalRange : 0
    }));

    const validData = dataWithNormalizedTime
        .map(p => ({ x: p.normalizedTime, y: p[metric] }))
        .filter(d => isFinite(d.x) && isFinite(d.y));

    if (validData.length < 2) {
        return globalTrend;
    }

    // Calculate R-squared
    const meanY = validData.reduce((sum, d) => sum + d.y, 0) / validData.length;
    const totalSS = validData.reduce((sum, d) => sum + Math.pow(d.y - meanY, 2), 0);
    
    const coefficients = helpers.linearRegression(validData);
    if (!coefficients) {
        return globalTrend;
    }

    const residualSS = validData.reduce((sum, d) => {
        const predicted = coefficients.slope * d.x + coefficients.intercept;
        return sum + Math.pow(d.y - predicted, 2);
    }, 0);

    const rSquared = totalSS > 0 ? 1 - (residualSS / totalSS) : 0;

    // If global trend explains less than 10% of variance, use EMA instead
    if (rSquared < 0.1) {
        console.log(`Low R-squared (${rSquared.toFixed(3)}) for ${metric}, using EMA instead`);
        return calculateExponentialMovingAverage(data, metric, 0.2);
    }

    return globalTrend;
};

// Main trend calculation router
export const calculateTrends = (data, metricKeys, algorithm, helpers) => {
    const trends = {};
    
    metricKeys.forEach(metric => {
        let trendValues;
        
        switch (algorithm) {
            case 'global':
                trendValues = calculateGlobalLinearTrend(data, metric, helpers);
                break;
            case 'ema':
                trendValues = calculateExponentialMovingAverage(data, metric, 0.3);
                break;
            case 'loess':
                trendValues = calculateLoessTrend(data, metric, 0.5, helpers);
                break;
            case 'segmented':
                trendValues = calculateSegmentedLinearTrend(data, metric, 5, helpers);
                break;
            case 'adaptive':
                trendValues = calculateAdaptiveTrend(data, metric, helpers);
                break;
            default:
                trendValues = data.map(point => point[metric]);
        }
        
        trends[metric] = trendValues;
    });
    
    return data.map((point, index) => {
        const newPoint = { ...point };
        
        metricKeys.forEach(metric => {
            const trendValue = trends[metric][index];
            
            // Apply bounds based on metric type
            if (metric.includes('correlation')) {
                newPoint[`${metric}Trend`] = Math.max(0, Math.min(1, trendValue));
            } else if (metric.includes('DTW') || metric.includes('diff')) {
                newPoint[`${metric}Trend`] = Math.max(0, trendValue);
            } else {
                newPoint[`${metric}Trend`] = trendValue;
            }
        });
        
        return newPoint;
    });
};

// Daily averages calculation
export const calculateDailyAverages = (data) => {
    const dateGroups = {};
    
    data.forEach(point => {
        const date = point.date;
        if (!dateGroups[date]) {
            dateGroups[date] = {
                date,
                count: 0,
                DTW: 0,
                correlation: 0
            };
        }
        
        dateGroups[date].count++;
        dateGroups[date].DTW += point.DTW || 0;
        dateGroups[date].correlation += point.correlation || 0;
    });

    return Object.values(dateGroups)
        .map(group => ({
            date: group.date,
            count: group.count,
            DTW: group.DTW / group.count,
            correlation: group.correlation / group.count
        }))
        .sort((a, b) => new Date(a.date) - new Date(b.date));
};